import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook for lazy loading images
 * Implements Intersection Observer for optimal performance
 */
export const useLazyLoad = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasLoaded) {
          setIsIntersecting(true);
          setHasLoaded(true);
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.01,
        ...options
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [hasLoaded, options]);

  return [ref, isIntersecting];
};

/**
 * LazyImage component with WebP support and placeholder
 */
export const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  placeholderSrc = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23121212" width="400" height="300"/%3E%3C/svg%3E',
  ...props 
}) => {
  const [ref, isIntersecting] = useLazyLoad();
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);

  const imageSrc = isIntersecting ? src : placeholderSrc;

  // Convert to WebP if supported
  const getOptimizedSrc = (url) => {
    if (!url || error) return placeholderSrc;
    
    // Check if URL already has format parameter
    if (url.includes('unsplash.com') || url.includes('images.')) {
      // Add WebP format and quality parameters for image services
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}auto=format&fit=crop&q=80&fm=webp`;
    }
    
    return url;
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <img
        src={getOptimizedSrc(imageSrc)}
        alt={alt}
        className={`${className} transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={() => setIsLoaded(true)}
        onError={() => {
          setError(true);
          setIsLoaded(true);
        }}
        loading="lazy"
        {...props}
      />
      {!isLoaded && (
        <div className="absolute inset-0 bg-[#121212] animate-pulse" />
      )}
    </div>
  );
};

/**
 * Optimized image component with srcset for responsive images
 */
export const ResponsiveImage = ({ 
  src, 
  alt, 
  className = '',
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  ...props 
}) => {
  const [ref, isIntersecting] = useLazyLoad();
  const [isLoaded, setIsLoaded] = useState(false);

  const generateSrcSet = (url) => {
    if (!url) return '';
    
    const widths = [400, 800, 1200, 1600];
    return widths
      .map(width => {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}w=${width}&fm=webp ${width}w`;
      })
      .join(', ');
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      {isIntersecting ? (
        <img
          src={src}
          srcSet={generateSrcSet(src)}
          sizes={sizes}
          alt={alt}
          className={`${className} transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsLoaded(true)}
          loading="lazy"
          {...props}
        />
      ) : (
        <div className={`${className} bg-[#121212] animate-pulse`} />
      )}
    </div>
  );
};

export default { useLazyLoad, LazyImage, ResponsiveImage };